/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { getData } from "../Services/getData";
import { urlEnum } from "../Utils/urlEnum";
import createConnectionForWebsocket from "../Utils/utils";
import AppInactivePage from "../Components/AppInactivePage";

const PUSHER_CHANNEL = "app-status";
const PUSHER_EVENT = "app.status.changed";

interface AppStatusContextValue {
  isActive: boolean;
  isLoading: boolean;
}

const AppStatusContext = createContext<AppStatusContextValue>({
  isActive: true,
  isLoading: true,
});

export function useAppStatus() {
  return useContext(AppStatusContext);
}

interface Props {
  children: ReactNode;
}

export function AppStatusProvider({ children }: Props) {
  const [isActive, setIsActive] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let pusher: any = null;
    let channel: any = null;

    async function checkInitialStatus() {
      try {
        const response = await getData(urlEnum.applicationStatus);
        setIsActive(response?.data?.is_active ?? true);
      } catch {
        // If the endpoint fails, assume active to avoid locking out users
        setIsActive(true);
      } finally {
        setIsLoading(false);
      }
    }

    function subscribeToStatusChanges() {
      pusher = createConnectionForWebsocket();
      channel = pusher.subscribe(PUSHER_CHANNEL);
      channel.bind(PUSHER_EVENT, () => {
        window.location.reload();
      });
    }

    checkInitialStatus();
    subscribeToStatusChanges();

    return () => {
      if (channel) {
        channel.unbind(PUSHER_EVENT);
        pusher.unsubscribe(PUSHER_CHANNEL);
      }
      if (pusher) {
        pusher.disconnect();
      }
    };
  }, []);

  return (
    <AppStatusContext.Provider value={{ isActive, isLoading }}>
      {children}
    </AppStatusContext.Provider>
  );
}

export function AppStatusGate({ children }: Props) {
  const { isActive, isLoading } = useAppStatus();

  if (isLoading) return null;
  if (!isActive) return <AppInactivePage />;

  return <>{children}</>;
}
